import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Clock, FileText, Loader2, Pencil, Plus, Send, Trash2, Upload, X } from "lucide-react";

const STATUS_BADGE = {
  TO_BILL: { label: "À facturer", cls: "bg-zinc-100 text-zinc-700" },
  PENDING_VALIDATION: { label: "En validation", cls: "bg-amber-100 text-amber-800" },
  BILLED: { label: "Facturé", cls: "bg-emerald-100 text-emerald-800" },
};

const fmtAmount = (n, currency) => {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${currency || ""}`.trim();
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

export default function MilestonesPanel({ mission, onChange }) {
  const { user } = useAuth();
  const milestones = mission?.milestones || [];
  const total = mission?.total_amount_ht || 0;
  const currency = mission?.currency || "";

  const canSubmit = ["CEO", "ADMIN", "MANAGER", "GESTIONNAIRE"].includes(user?.role);
  const canValidate = ["CEO", "GESTIONNAIRE"].includes(user?.role);
  const isMissionManager = mission?.manager_id === user?.user_id;
  // Manager can submit only on their own missions (matches backend rule).
  const canSubmitThis = canSubmit && (user?.role !== "MANAGER" || isMissionManager);

  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitMilestone, setSubmitMilestone] = useState(null);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);  // [{file, uploaded?}]
  // Iter-229 — additional documents (006-Autres documents) with a display name each.
  const [otherFiles, setOtherFiles] = useState([]);  // [{file, name}]
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(null);

  // Iter-188 — inline edit of TO_BILL milestones (CEO / GESTIONNAIRE).
  const canEdit = ["CEO", "GESTIONNAIRE"].includes(user?.role);
  const [editing, setEditing] = useState(null); // milestone_id being edited
  const [editForm, setEditForm] = useState({ name: "", percentage: "", planned_date: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  // Iter-189 — add / delete unbilled milestones (CEO / GESTIONNAIRE).
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", percentage: "", planned_date: "" });
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const totalPct = milestones.reduce((s, m) => s + (Number(m.percentage) || 0), 0);
  const remainingPct = Math.round((100 - totalPct) * 100) / 100;

  const openEdit = (ms) => {
    setEditing(ms.milestone_id);
    setEditForm({
      name: ms.name || "",
      percentage: String(ms.percentage ?? ""),
      planned_date: ms.planned_date || "",
    });
  };
  const cancelEdit = () => { setEditing(null); setSavingEdit(false); };
  const saveEdit = async (ms) => {
    const payload = {};
    if ((editForm.name || "").trim() !== (ms.name || "")) payload.name = editForm.name.trim();
    const newPct = parseFloat(editForm.percentage);
    if (!isNaN(newPct) && newPct !== ms.percentage) payload.percentage = newPct;
    if ((editForm.planned_date || "") !== (ms.planned_date || "")) payload.planned_date = editForm.planned_date || "";
    if (Object.keys(payload).length === 0) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/missions/${mission.mission_id}/milestones/${ms.milestone_id}`, payload);
      toast.success("Palier mis à jour");
      cancelEdit();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAdd = async () => {
    const pct = parseFloat(addForm.percentage);
    if (!(addForm.name || "").trim()) return toast.error("Nom obligatoire");
    if (!pct || pct <= 0 || pct > 100) return toast.error("Pourcentage > 0 et ≤ 100");
    setAdding(true);
    try {
      await api.post(`/missions/${mission.mission_id}/milestones`, {
        name: addForm.name.trim(),
        percentage: pct,
        planned_date: addForm.planned_date || null,
      });
      toast.success("Palier ajouté");
      setAddOpen(false);
      setAddForm({ name: "", percentage: "", planned_date: "" });
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (ms) => {
    if (!window.confirm(`Supprimer le palier "${ms.name}" (${ms.percentage}%) ?`)) return;
    setDeletingId(ms.milestone_id);
    try {
      await api.delete(`/missions/${mission.mission_id}/milestones/${ms.milestone_id}`);
      toast.success("Palier supprimé");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const openSubmit = (ms) => {
    setSubmitMilestone(ms);
    setComment("");
    setFiles([]);
    setOtherFiles([]);
    setSubmitOpen(true);
  };

  const handleSubmit = async () => {
    if (!comment.trim()) return toast.error("Commentaire obligatoire");
    if (otherFiles.some((f) => !(f.name || "").trim())) return toast.error("Chaque document additionnel doit avoir un nom");
    setSubmitting(true);
    try {
      // 1. Upload each file to 004-Livrables (one POST per file).
      const uploaded = [];
      for (const item of files) {
        const form = new FormData();
        form.append("file", item.file);
        const r = await api.post(
          `/missions/${mission.mission_id}/milestones/${submitMilestone.milestone_id}/upload-deliverable`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        uploaded.push({ file_id: r.data.file_id, name: r.data.name, web_view_link: r.data.web_view_link });
      }
      // 1b. Iter-229 — upload each additional document to 006-Autres documents.
      const uploadedOthers = [];
      for (const item of otherFiles) {
        const form = new FormData();
        form.append("file", item.file);
        form.append("name", item.name.trim());
        const r = await api.post(
          `/missions/${mission.mission_id}/milestones/${submitMilestone.milestone_id}/upload-other-file`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        uploadedOthers.push({ file_id: r.data.file_id, name: r.data.name, web_view_link: r.data.web_view_link });
      }
      // 2. Submit to billing with comment + deliverables + other files.
      const r2 = await api.post(
        `/missions/${mission.mission_id}/milestones/${submitMilestone.milestone_id}/submit-billing`,
        { comment: comment.trim(), deliverables: uploaded, other_files: uploadedOthers },
      );
      const d = r2.data || {};
      if (d.auto_validated) {
        toast.success(d.shadow_mode
          ? `Palier facturé (shadow mode — pas d'email envoyé). N° facture créée.`
          : `Palier facturé automatiquement et email envoyé.`);
      } else {
        toast.success("Palier soumis pour validation");
      }
      setSubmitOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidate = async (ms) => {
    if (!window.confirm(`Valider le palier "${ms.name}" et générer la facture ?`)) return;
    setValidating(ms.milestone_id);
    try {
      const r = await api.post(
        `/missions/${mission.mission_id}/milestones/${ms.milestone_id}/validate-billing`,
      );
      toast.success(`Facture ${r.data.invoice_id} générée`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de validation");
    } finally {
      setValidating(null);
    }
  };

  if (mission?.type !== "FORFAIT") {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-8 text-sm text-zinc-500 text-center" data-testid="milestones-not-forfait">
        Les paliers de facturation ne sont disponibles que pour les missions Forfait.
      </div>
    );
  }

  return (
    <div data-testid="milestones-panel">
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-4">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-zinc-500">Forfait · contract value</div>
            <div className="font-heading text-2xl font-bold mt-1" data-testid="milestones-total-amount">
              {fmtAmount(total, currency)}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-right text-xs text-zinc-500 font-mono" data-testid="milestones-pct-summary">
              {milestones.length} palier(s) ·
              <span className={Math.abs(totalPct - 100) < 0.01 ? " text-emerald-700" : " text-amber-700"}>
                {" "}{totalPct.toFixed(2)}% défini
              </span>
              {Math.abs(totalPct - 100) >= 0.01 && (
                <span className="text-zinc-500"> · reste {remainingPct}%</span>
              )}
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => { setAddForm({ name: "", percentage: remainingPct > 0 ? String(remainingPct) : "", planned_date: "" }); setAddOpen(true); }}
                data-testid="milestone-add-btn"
                className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600"
              >
                <Plus size={13} /> Ajouter un palier
              </button>
            )}
          </div>
        </div>
        {Math.abs(totalPct - 100) >= 0.01 && (
          <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="milestones-sum-warning">
            {totalPct < 100
              ? `La somme actuelle est de ${totalPct.toFixed(2)}%. Ajoute ou édite un palier pour atteindre 100% — sinon une partie du contrat ne sera pas facturée.`
              : `La somme actuelle est de ${totalPct.toFixed(2)}% (dépassement de ${(totalPct - 100).toFixed(2)}%). Réduis un palier non facturé.`}
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="milestones-list">
        {milestones.map((ms) => {
          const badge = STATUS_BADGE[ms.status] || STATUS_BADGE.TO_BILL;
          return (
            <div key={ms.milestone_id} className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`milestone-row-${ms.milestone_id}`}>
              {editing === ms.milestone_id ? (
                <div className="space-y-3" data-testid={`milestone-edit-form-${ms.milestone_id}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                    <div className="sm:col-span-6">
                      <Label className="text-[10px]">Nom du palier</Label>
                      <Input
                        data-testid={`milestone-edit-name-${ms.milestone_id}`}
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[10px]">% (somme=100)</Label>
                      <Input
                        data-testid={`milestone-edit-pct-${ms.milestone_id}`}
                        type="number" min="0" max="100" step="0.01"
                        value={editForm.percentage}
                        onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })}
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <Label className="text-[10px]">Date prévue</Label>
                      <Input
                        data-testid={`milestone-edit-date-${ms.milestone_id}`}
                        type="date"
                        value={editForm.planned_date || ""}
                        onChange={(e) => setEditForm({ ...editForm, planned_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={savingEdit}
                      className="px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50"
                    >Annuler</button>
                    <button
                      type="button"
                      onClick={() => saveEdit(ms)}
                      disabled={savingEdit}
                      data-testid={`milestone-edit-save-${ms.milestone_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
                    >
                      {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      Enregistrer
                    </button>
                  </div>
                </div>
              ) : (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-semibold text-base">{ms.name}</div>
                    <span className={`text-[10px] uppercase tracking-[0.16em] font-mono px-2 py-0.5 rounded ${badge.cls}`} data-testid={`milestone-status-${ms.milestone_id}`}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-zinc-500 font-mono">{ms.percentage}%</span>
                  </div>
                  <div className="text-sm text-zinc-600 mt-1 font-mono">
                    {fmtAmount(ms.amount_ht, currency)} HT · échéance : {fmtDate(ms.planned_date)}
                  </div>
                  {ms.comment && (
                    <div className="mt-3 text-[12px] text-zinc-700 bg-zinc-50 border border-zinc-100 rounded px-3 py-2 max-w-2xl">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Commentaire</span>
                      <div className="mt-1">{ms.comment}</div>
                    </div>
                  )}
                  {((ms.deliverables || []).length > 0 || (ms.other_files || []).length > 0) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(ms.deliverables || []).map((d, i) => (
                        <a
                          key={`d${i}`}
                          href={d.web_view_link || "#"}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`milestone-deliverable-${ms.milestone_id}-${i}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 text-zinc-700"
                        >
                          <FileText size={11} /> {d.name || "Fichier"}
                        </a>
                      ))}
                      {(ms.other_files || []).map((d, i) => (
                        <a
                          key={`o${i}`}
                          href={d.web_view_link || "#"}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`milestone-other-file-${ms.milestone_id}-${i}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800"
                          title="Document additionnel (006-Autres documents)"
                        >
                          <FileText size={11} /> {d.name || "Document"}
                        </a>
                      ))}
                    </div>
                  )}
                  {ms.invoice_id && (
                    <div className="mt-2 text-xs text-emerald-700 font-mono flex items-center gap-1">
                      <CheckCircle2 size={11} /> Facture {ms.invoice_id}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {ms.status === "TO_BILL" && canEdit && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(ms)}
                        data-testid={`milestone-edit-btn-${ms.milestone_id}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50 text-zinc-700"
                        title="Modifier le palier"
                      >
                        <Pencil size={13} /> Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(ms)}
                        disabled={deletingId === ms.milestone_id}
                        data-testid={`milestone-delete-btn-${ms.milestone_id}`}
                        className="inline-flex items-center justify-center w-9 h-9 bg-white border border-zinc-300 text-zinc-500 rounded-md hover:bg-red-50 hover:text-red-700 hover:border-red-200 disabled:opacity-60"
                        title="Supprimer le palier"
                      >
                        {deletingId === ms.milestone_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      </button>
                    </div>
                  )}
                  {ms.status === "TO_BILL" && canSubmitThis && (
                    <button
                      onClick={() => openSubmit(ms)}
                      data-testid={`milestone-bill-btn-${ms.milestone_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600"
                    >
                      <Send size={13} /> Facturer ce palier
                    </button>
                  )}
                  {ms.status === "PENDING_VALIDATION" && canValidate && (
                    <button
                      onClick={() => handleValidate(ms)}
                      disabled={validating === ms.milestone_id}
                      data-testid={`milestone-validate-btn-${ms.milestone_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 bg-emerald-700 text-white text-sm rounded-md hover:bg-emerald-800 disabled:opacity-60"
                    >
                      {validating === ms.milestone_id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      Valider & générer facture
                    </button>
                  )}
                  {ms.status === "PENDING_VALIDATION" && !canValidate && (
                    <div className="text-[11px] text-amber-700 font-mono flex items-center gap-1 px-2">
                      <Clock size={11} /> En attente validation CEO / Gestionnaire
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          );
        })}
        {milestones.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-500" data-testid="milestones-empty">
            Aucun palier défini pour cette mission.
          </div>
        )}
      </div>

      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent data-testid="milestone-submit-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Facturer le palier · {submitMilestone?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-zinc-500 font-mono">
              {fmtAmount(submitMilestone?.amount_ht, currency)} HT · {submitMilestone?.percentage}%
            </div>
            <div>
              <Label htmlFor="ms-comment">Commentaire <span className="text-red-600">*</span></Label>
              <Textarea
                id="ms-comment"
                data-testid="milestone-submit-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Détails de la livraison, contexte, périmètre couvert…"
                rows={4}
              />
            </div>
            <div>
              <Label>Livrables (optionnel)</Label>
              <input
                type="file"
                multiple
                data-testid="milestone-submit-files"
                onChange={(e) => setFiles(Array.from(e.target.files || []).map((f) => ({ file: f })))}
                className="block w-full text-sm text-zinc-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-zinc-100 file:text-zinc-900 file:cursor-pointer hover:file:bg-zinc-200"
              />
              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <span key={i} className="text-[11px] px-2 py-1 rounded border border-zinc-200 bg-zinc-50 inline-flex items-center gap-1">
                      <Upload size={10} /> {f.file.name}
                      <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-600"><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-zinc-500 mt-1">Les fichiers seront uploadés dans le dossier Drive 004-Livrables de la mission.</div>
            </div>
            <div>
              <Label>Autres documents (optionnel)</Label>
              <input
                type="file"
                multiple
                data-testid="milestone-submit-other-files"
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []).map((f) => ({
                    file: f,
                    name: f.name.replace(/\.[^.]+$/, ""),
                  }));
                  setOtherFiles([...otherFiles, ...picked]);
                  e.target.value = "";
                }}
                className="block w-full text-sm text-zinc-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-zinc-100 file:text-zinc-900 file:cursor-pointer hover:file:bg-zinc-200"
              />
              {otherFiles.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {otherFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`milestone-other-file-row-${i}`}>
                      <FileText size={12} className="text-zinc-400 shrink-0" />
                      <Input
                        data-testid={`milestone-other-file-name-${i}`}
                        value={f.name}
                        onChange={(e) => setOtherFiles(otherFiles.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                        placeholder="Nom du document *"
                        className="h-8 text-sm"
                      />
                      <span className="text-[10px] text-zinc-400 font-mono truncate max-w-[120px] shrink-0">{f.file.name}</span>
                      <button
                        onClick={() => setOtherFiles(otherFiles.filter((_, j) => j !== i))}
                        data-testid={`milestone-other-file-remove-${i}`}
                        className="text-zinc-400 hover:text-red-600 shrink-0"
                      ><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-zinc-500 mt-1">
                Chaque document doit être nommé. Uploadés dans « 006-Autres documents » du Drive mission, joints à la ligne de facturation ET à l'email de facture.
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setSubmitOpen(false)}
              disabled={submitting}
              className="px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50"
            >Annuler</button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !comment.trim()}
              data-testid="milestone-submit-confirm"
              className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Soumettre
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="milestone-add-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un palier</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-zinc-500 font-mono">
              Reste {remainingPct.toFixed(2)}% libre · contrat {fmtAmount(total, currency)}
            </div>
            <div>
              <Label htmlFor="add-ms-name">Nom du palier <span className="text-red-600">*</span></Label>
              <Input
                id="add-ms-name"
                data-testid="milestone-add-name"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="Nouveau jalon…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="add-ms-pct">% <span className="text-red-600">*</span></Label>
                <Input
                  id="add-ms-pct"
                  data-testid="milestone-add-pct"
                  type="number" min="0" max="100" step="0.01"
                  value={addForm.percentage}
                  onChange={(e) => setAddForm({ ...addForm, percentage: e.target.value })}
                />
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  ≈ {((parseFloat(addForm.percentage) || 0) * total / 100).toFixed(0)} {currency}
                </div>
              </div>
              <div>
                <Label htmlFor="add-ms-date">Date prévue</Label>
                <Input
                  id="add-ms-date"
                  data-testid="milestone-add-date"
                  type="date"
                  value={addForm.planned_date || ""}
                  onChange={(e) => setAddForm({ ...addForm, planned_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAddOpen(false)} disabled={adding} className="px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50">Annuler</button>
            <button
              onClick={handleAdd}
              disabled={adding}
              data-testid="milestone-add-confirm"
              className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
            >
              {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Ajouter
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
